import type { ComponentProps, ReactNode } from 'react';

import type { ColorName, Style } from './buttonClasses';
import type { Size } from './types';
import Button from './Button';
import ButtonGroup from './ButtonGroup';

type BadgeWButtonType = 'standard' | 'success' | 'dangerous' | 'warning' | 'medium' | 'high' | 'low';

export type BadgeWButtonProps = {
  onButtonClick: ComponentProps<'div'>['onClick'];
  /** What the icon button does, e.g. "Remove tag Investor" — its accessible name and tooltip. The
   *  button is icon-only, so without this a screen reader announces just "button". */
  buttonLabel: string;
  disabled?: boolean;
  bIcon: ReactNode;
  children?: ReactNode;
  size?: 'xs' | 'sm';
  type?: BadgeWButtonType;
  className?: string;
  /** Additional className for the action button (e.g., for hover effects) */
  buttonClassName?: string;
};

function mapTypeToColorTone(type?: BadgeWButtonType): { color: ColorName; tone: Style } {
  switch (type) {
    case 'standard':
      return { color: 'blue', tone: 'filled' };
    case 'success':
      return { color: 'green', tone: 'soft' };
    case 'dangerous':
      return { color: 'red', tone: 'soft' };
    case 'warning':
    case 'medium':
      return { color: 'yellow', tone: 'soft' };
    case 'high':
      return { color: 'yellow', tone: 'filled' };
    case 'low':
      return { color: 'gray', tone: 'filled' };
    default:
      return { color: 'gray', tone: 'filled' };
  }
}

function mapSize(size?: 'xs' | 'sm'): Size {
  return size === 'xs' ? '3xs' : size === 'sm' ? '2xs' : 'xs';
}

export default function BadgeWButton({
  onButtonClick,
  buttonLabel,
  className,
  buttonClassName,
  bIcon,
  disabled,
  children,
  size,
  type,
}: BadgeWButtonProps) {
  const buttonSize = mapSize(size);
  const { color, tone } = mapTypeToColorTone(type);

  return (
    <ButtonGroup size={buttonSize} color={color} tone={tone} shape="pill" className={className}>
      <Button readOnly>{children}</Button>
      <Button
        icon={bIcon}
        aria-label={buttonLabel}
        tooltip={buttonLabel}
        disabled={disabled}
        onClick={onButtonClick as ComponentProps<'button'>['onClick']}
        className={buttonClassName}
      />
    </ButtonGroup>
  );
}
